// ─── Shared Types ────────────────────────────────────────────────────────────

export interface Attachment {
  id: string;
  entityType: string;
  entityId: string;
  filename: string;
  mimeType: string;
  size: number;
  createdAt: string;
  url: string;
}

// ─── Knowledge Base & API Reference Types ────────────────────────────────────

export interface DocNode {
  id: string;
  parentId: string | null;
  nodeType: "category" | "page" | "section" | "api_endpoint" | "card";
  title: string;
  slug: string;
  sectionType: string | null;
  body: string | null;
  visibility: "public" | "internal";
  order: string[];
  createdAt: string;
  updatedAt: string;
  children?: DocNode[];
  // Support / FAQ typed metadata surfaced as top-level fields by the
  // node serializer. Present only when the section_type is support
  // or faq; safe to read as undefined elsewhere.
  summary?: string;
  tags?: string[];
  modules?: string[];
  featured?: boolean;
  lastEdit?: {
    userId: string | null;
    userEmail: string | null;
    userName: string | null;
    at: string;
  } | null;
}

export interface ApiSpec {
  id: string;
  title: string;
  description: string | null;
  version: string | null;
  format: "yaml" | "json";
  createdAt: string;
  updatedAt: string;
}

export interface ApiParameter {
  name: string;
  in: "path" | "query" | "header" | "cookie";
  required: boolean;
  description: string | null;
  schema: JsonSchema;
}

export interface JsonSchema {
  // May be a union array (e.g. ["number", "null"]) in OpenAPI 3.1 specs.
  // Read it through the helpers in utils/schemaType rather than directly.
  type?: string | string[];
  properties?: Record<string, JsonSchema>;
  items?: JsonSchema;
  required?: string[];
  description?: string;
  enum?: string[];
  format?: string;
  example?: unknown;
  contentType?: string;
}

export interface ApiResponse {
  description: string;
  schema: JsonSchema | null;
}

export type AuthType = "none" | "partner_token" | "client_token" | "user_token";

export interface ApiEndpoint {
  id: string;
  tag: string;
  method: string;
  path: string;
  summary: string | null;
  description: string | null;
  body: string | null;
  parameters: ApiParameter[];
  requestBody: JsonSchema | null;
  responses: Record<string, ApiResponse>;
  authType: AuthType;
  lastEdit?: {
    userId: string | null;
    userEmail: string | null;
    userName: string | null;
    at: string;
  } | null;
}

export interface ApiEndpointGroup {
  tag: string;
  tagId?: string;
  tagSlug?: string;
  tagBody?: string | null;
  endpoints: ApiEndpoint[];
}

export interface DocSearchResult {
  id: string;
  title: string;
  sectionType: string | null;
  snippet: string;
  slugPath: string;
  rank: number;
  lastEdit?: {
    userId: string | null;
    userEmail: string | null;
    userName: string | null;
    at: string;
  } | null;
}

export interface BulkImportPayload {
  nodes: ImportNode[];
}

export interface ImportNode {
  path: string;
  title: string;
  sectionType?: string;
  body?: string;
  order?: number;
  children?: ImportNode[];
}

export interface BulkImportResult {
  created: number;
  skipped: number;
  errors: { path: string; reason: string }[];
}

// ─── CMS Types (Branched Content Management) ────────────────────────────────

export interface Branch {
  id: string;
  name: string;
  slug: string;
  isProtected?: boolean;
  sequence?: number;
  headCommitId?: string | null;
  source?: "local" | "remote";
  fetched?: boolean;
  localRef?: string;
  isActive?: boolean;
  ahead?: number;
  behind?: number;
  headCommit?: string;
  tracksRemote?: string;
  remoteBranchId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface MergePreviewConflict {
  slugPath: string;
  reason: string;
  liveTitle?: string;
  liveBody?: string;
  draftTitle?: string;
  draftBody?: string;
  forkTitle?: string;
}

export interface DiffSummary {
  added: { slugPath: string; title: string }[];
  modified: { slugPath: string; title: string }[];
  deleted: { slugPath: string; title: string }[];
  addedCount: number;
  modifiedCount: number;
  deletedCount: number;
  totalChanges: number;
}

export interface MergePreviewResult {
  fastForward: boolean;
  sourceHead: string | null;
  targetHead: string | null;
  sourceBranchId: string;
  targetBranchId: string;
  sourceSequence?: number | null;
  targetSequence?: number | null;
  message?: string;
  diffSummary: DiffSummary;
  conflicts: MergePreviewConflict[];
  cleanFiles: { slugPath: string; title: string }[];
  newFiles: { slugPath: string; title: string }[];
  deletedFiles: { slugPath: string }[];
  draftForkSequence?: number;
  liveSequence?: number;
  lcaBranchId?: string;
  lcaSequence?: number;
}

export interface DiffTreeNode {
  slug: string;
  title: string;
  slugPath: string;
  nodeType: "category" | "page" | "section" | "api_endpoint" | "card";
  status: "unchanged" | "added" | "removed" | "updated";
  orderChanged: boolean;
  children: DiffTreeNode[];
}

export interface SectionDiff {
  sectionId: string;
  slug: string;
  title: string;
  sectionType: SectionType;
  added: number;
  removed: number;
  updated: number;
  totalChanges: number;
  sectionStatus: "added" | "removed" | "unchanged";
  liveTree: DiffTreeNode[];
  draftTree: DiffTreeNode[];
}

export interface BranchDiffResult {
  sourceBranchId?: string;
  targetBranchId?: string;
  sourceCommitId?: string | null;
  targetCommitId?: string | null;
  added: { slugPath: string; title: string }[];
  modified: { slugPath: string; title: string }[];
  deleted: { slugPath: string; title: string }[];
  updated?: { slugPath: string; title: string; liveTitle?: string }[];
  removed?: { slugPath: string; title: string }[];
  orderChanges?: { location: string; title?: string; liveOrder: string[]; draftOrder: string[] }[];
  sections?: SectionDiff[];
  commits?: { sequence: number; action: string; slugPath: string; contentType: string; createdAt: string | null }[];
  addedCount: number;
  modifiedCount: number;
  deletedCount: number;
  totalChanges: number;
}

// KEEP IN SYNC with the backend's KNOWN list in
// cortex-api/src/routes/cms_sections.py (`_section_from_node`) and the
// snapshot/branch-diff allowlists. The two new types — 'support' and
// 'faq' — drive their own Studio sidebars, page renderers, public read
// API, and 3-way merge of typed metadata. The TS type historically
// lied (string-narrowed to the old three) which let support/faq sneak
// through at runtime via DocsLayout's untyped dispatch; widening the
// union just makes the type system reflect the real surface.
export type SectionType = "content" | "api" | "presentations" | "support" | "faq" | "release-notes";

export interface Section {
  id: string;
  projectId: string;
  slug: string;
  title: string;
  sectionType: SectionType;
  visibility?: "public" | "internal";
  icon: string | null;
  homePage: string | null;
  baseUrl?: string;
  metadata?: { baseUrl?: string } | null;
  createdAt: string;
  updatedAt: string;
}

export interface ContentNode {
  id: string;
  branchId: string;
  parentId: string | null;
  nodeType: "category" | "page" | "section" | "api_endpoint" | "card";
  title: string;
  slug: string;
  sectionType: string | null;
  body: string | null;
  /** API spec (method, path, parameters, etc.) — only present for api_endpoint nodes */
  spec?: Record<string, any> | null;
  /** API base URL — present on sections and categories in API sections */
  baseUrl?: string;
  visibility: "public" | "internal";
  order: string[];
  createdAt: string;
  updatedAt: string;
  children?: ContentNode[];
  /** Sidebar-only fields from snapshot tree (no body/spec) */
  method?: string;
  apiPath?: string;
  /** Whether this node has body content — set by backend, used for navigation decisions */
  hasBody?: boolean;
  /** Support-section metadata, surfaced by the backend serializer from
   * content_nodes.metadata. Only populated for pages inside a
   * `section_type='support'` subtree. */
  summary?: string;
  tags?: string[];
  modules?: string[];
  featured?: boolean;
  /** Author + timestamp of the commit that last actually changed this node's
   * content. Denormalized from commit_tree.last_edited_* by tree_rebuild.
   * Null until the node has been committed at least once on the current branch. */
  lastEdit?: {
    userId: string | null;
    userEmail: string | null;
    userName: string | null;
    at: string;
  } | null;
}

export interface ContentTreeResponse {
  tree: ContentNode[];
  stateHashes: Record<string, string>;
}

export interface MutationResponse {
  node: ContentNode;
  stateHash: string;
}

export interface ConflictResponse {
  error: "conflict";
  message: string;
  currentHash: string;
}

// ─── Shared Commit Model Types ──────────────────────────────────────────────

export interface CommitChange {
  id: string;
  action: "create" | "update" | "delete";
  filePath: string;
  contentHash: string | null;
  specHash?: string | null;
}

export interface ProjectCommit {
  id: string;
  parentId: string | null;
  sequence: number;
  projectId: string;
  message: string | null;
  createdAt: string | null;
  changes: CommitChange[];
}

/* ── Spec merge types (api_endpoint rebase conflicts) ─────────────── */

export interface SpecConflict {
  key: string;
  base: any;
  source: any;
  dest: any;
}

export interface SpecMergeResult {
  merged: Record<string, any>;
  conflicts: SpecConflict[];
  hasConflicts: boolean;
}


// ─── Presentation Card Types ────────────────────────────────────────────────

export type LeafBlockType =
  | "h1" | "h2" | "h3" | "text" | "image" | "code" | "quote"
  | "callout" | "divider" | "spacer" | "video" | "embed"
  | "mermaid" | "toggle" | "table" | "bullets";

export type LayoutBlockType =
  | "columns" | "stats" | "timeline" | "gallery" | "comparison"
  | "features" | "pricing" | "steps" | "logos" | "testimonial";

export type BlockType = LeafBlockType | LayoutBlockType;

export const LEAF_BLOCK_TYPES: LeafBlockType[] = [
  "h1", "h2", "h3", "text", "image", "code", "quote",
  "callout", "divider", "spacer", "video", "embed",
  "mermaid", "toggle", "table", "bullets",
];

export const LAYOUT_BLOCK_TYPES: LayoutBlockType[] = [
  "columns", "stats", "timeline", "gallery", "comparison",
  "features", "pricing", "steps", "logos", "testimonial",
];

export interface CardRow {
  [key: string]: any;
}

export interface CardMeta {
  background?: string;
  cardStyle?: string;
  transition?: string;
  speakerNotes?: string;
  styles?: Record<string, string>;
}

export interface DeckConfig {
  theme?: string;
  fonts?: { heading?: string; body?: string };
  palette?: Record<string, string>;
  defaultCardStyle?: string;
  defaultTransition?: string;
}

/** Default Deck_Config values */
export const DEFAULT_DECK_CONFIG: DeckConfig = {
  theme: "default",
  fonts: { heading: "Inter", body: "Inter" },
  palette: {
    primary: "#4f46e5",
    secondary: "#818cf8",
    background: "#ffffff",
    text: "#1e293b",
  },
  defaultCardStyle: "light",
  defaultTransition: "slide",
};

/** Default style variants per layout block type */
export const LAYOUT_STYLE_VARIANTS: Record<LayoutBlockType, string[]> = {
  columns: ["plain", "cards", "stats", "feature"],
  timeline: ["vertical", "horizontal", "alternating"],
  gallery: ["grid", "masonry", "carousel"],
  stats: ["plain", "cards", "highlight"],
  comparison: ["table", "cards", "side-by-side"],
  features: ["grid", "list", "cards"],
  pricing: ["cards", "table", "compact"],
  steps: ["numbered", "timeline", "cards"],
  logos: ["grid", "row", "carousel"],
  testimonial: ["cards", "quote", "minimal"],
};

/** Resolve effective config: Card_Meta overrides Deck_Config defaults */
export function resolveCardConfig(deckConfig: DeckConfig, cardMeta: CardMeta): CardMeta & DeckConfig {
  return {
    ...DEFAULT_DECK_CONFIG,
    ...deckConfig,
    ...cardMeta,
    fonts: { ...DEFAULT_DECK_CONFIG.fonts, ...deckConfig.fonts },
    palette: { ...DEFAULT_DECK_CONFIG.palette, ...deckConfig.palette },
    styles: cardMeta.styles ?? {},
  };
}

/** Get the style variant for a block type from Card_Meta */
export function getBlockVariant(blockType: string, cardMeta: CardMeta): string {
  return cardMeta.styles?.[blockType] ?? LAYOUT_STYLE_VARIANTS[blockType as LayoutBlockType]?.[0] ?? "plain";
}

/** Get the block type key from a CardRow */
export function getRowBlockType(row: CardRow): BlockType | null {
  const keys = Object.keys(row);
  if (keys.length !== 1) return null;
  return keys[0] as BlockType;
}
